import { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { submitCallRequest } from "../lib/api";
import { FadeUp } from "./Reveal";

const TYPES = [
  "Intro call — 30 min",
  "Coaching discovery — 45 min",
  "Team clinic consultation — 45 min",
];

const STEPS = [
  ["01", "Tell Eric what you need", "Pick a call type and leave a line about what's going on."],
  ["02", "He replies with times", "Eric emails you personally to find a slot that works."],
  ["03", "Show up and talk", "No prep required — bring what's actually going on."],
];

export default function BookCall() {
  const [form, setForm] = useState({ name: "", email: "", call_type: TYPES[0], notes: "" });
  const [phase, setPhase] = useState("form");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const valid = form.name.trim().length > 1 && /\S+@\S+\.\S+/.test(form.email);

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || phase === "saving") return;
    setPhase("saving");
    setError("");
    try {
      await submitCallRequest(form);
      setPhase("booked");
    } catch {
      setPhase("form");
      setError("Couldn't send your request — please try again.");
    }
  };

  return (
    <section id="book-a-call" data-testid="book-call" className="border-t border-line py-24 md:py-32">
      <div className="mx-auto grid max-w-[1440px] gap-14 px-6 md:grid-cols-12 md:px-10">
        <div className="md:col-span-5">
          <FadeUp>
            <p className="eyebrow text-ember">Book a call</p>
            <h2 className="mt-6 font-display text-4xl font-extrabold leading-[1.02] tracking-tight md:text-5xl">
              Skip the inbox.
            </h2>
            <p className="mt-6 max-w-md leading-relaxed text-steel">
              Tell Eric what's on your mind and he'll reply personally with times that
              work — no calendars, no phone tag.
            </p>
          </FadeUp>
          <div className="mt-12 space-y-8">
            {STEPS.map(([n, t, d], i) => (
              <FadeUp key={n} delay={i * 0.08} className="flex gap-5">
                <span className="font-mono text-[11px] tracking-[0.2em] text-ember">{n}</span>
                <div>
                  <h3 className="font-display text-lg font-bold tracking-tight">{t}</h3>
                  <p className="mt-1 text-sm leading-relaxed text-steel">{d}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>

        <div className="md:col-span-7" data-testid="book-call-panel">
          {phase === "booked" ? (
            <div className="flex h-full min-h-[420px] flex-col items-start justify-center border border-line p-10 md:p-14">
              <span className="flex h-14 w-14 items-center justify-center border border-ember text-ember" data-testid="book-call-success-icon">
                <Check className="h-6 w-6" />
              </span>
              <h3 className="mt-8 font-display text-4xl font-extrabold tracking-tight" data-testid="book-call-success-title">
                Request received.
              </h3>
              <p className="mt-4 leading-relaxed text-steel">
                {form.call_type}. Eric will email you at {form.email} to find a time that
                works.
              </p>
              <button
                data-testid="book-call-again"
                onClick={() => {
                  setForm({ name: "", email: "", call_type: TYPES[0], notes: "" });
                  setPhase("form");
                }}
                className="btn-outline mt-10"
              >
                Request another call
              </button>
            </div>
          ) : (
            <FadeUp delay={0.1}>
              <form onSubmit={submit} data-testid="book-call-form" className="border border-line p-8 md:p-10">
                <label className="block">
                  <span className="eyebrow text-ink/50">What kind of call</span>
                  <select data-testid="book-call-type" className="field-input mt-2 cursor-pointer" value={form.call_type} onChange={set("call_type")}>
                    {TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="mt-8 grid gap-6 md:grid-cols-2">
                  <label className="block">
                    <span className="eyebrow text-ink/50">Name *</span>
                    <input data-testid="book-call-name" className="field-input mt-2" placeholder="Your name" value={form.name} onChange={set("name")} />
                  </label>
                  <label className="block">
                    <span className="eyebrow text-ink/50">Email *</span>
                    <input data-testid="book-call-email" type="email" className="field-input mt-2" placeholder="you@company.com" value={form.email} onChange={set("email")} />
                  </label>
                  <label className="block md:col-span-2">
                    <span className="eyebrow text-ink/50">What should Eric know first? (optional)</span>
                    <textarea
                      data-testid="book-call-notes"
                      className="field-input mt-2 min-h-[70px] resize-y"
                      placeholder="A line or two is plenty."
                      value={form.notes}
                      onChange={set("notes")}
                    />
                  </label>
                </div>

                {error && (
                  <p data-testid="book-call-error" className="mt-6 text-sm text-forge">
                    {error}
                  </p>
                )}
                <button type="submit" data-testid="book-call-submit" disabled={!valid || phase === "saving"} className={`btn mt-8 ${valid ? "btn-ink" : "cursor-not-allowed bg-ink/10 text-ink/40"}`}>
                  {phase === "saving" ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending…
                    </>
                  ) : (
                    <>Request a call — Eric confirms by email</>
                  )}
                </button>
              </form>
            </FadeUp>
          )}
        </div>
      </div>
    </section>
  );
}
